import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Form,
  Table,
  Modal,
  message,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Spin,
  Select,
  InputNumber,
  DatePicker,
  Divider,
  Statistic,
  Tabs,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  BarChartOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getAllInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  assignInventory,
  releaseInventory,
  getInventoryReport,
  getItemAssignments,
  reassignInventory,
} from '../services/inventoryService';
import { listUsers } from '../services/userService';
import { themeConfig } from '../config/theme';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const InventoryManagement = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [items, setItems] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('items');

  // Item Modal State
  const [itemForm] = Form.useForm();
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemSubmitting, setItemSubmitting] = useState(false);

  // Assign Modal State
  const [assignForm] = Form.useForm();
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [assigningItem, setAssigningItem] = useState(null);
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  // Reassignment Modal State
  const [reassignForm] = Form.useForm();
  const [isReassignModalVisible, setIsReassignModalVisible] = useState(false);
  const [reassigningData, setReassigningData] = useState(null);
  const [reassignSubmitting, setReassignSubmitting] = useState(false);

  // View Assignments Modal State
  const [isAssignmentsModalVisible, setIsAssignmentsModalVisible] = useState(false);
  const [itemAssignments, setItemAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [selectedItemForAssignments, setSelectedItemForAssignments] = useState(null);

  // Report Modal State
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    itemId: null,
    workerMobileNumber: null,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    fetchItems();
    fetchWorkers();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await getAllInventoryItems();
      setItems(response.items || []);
    } catch (error) {
      messageApi.error('Failed to fetch inventory items');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await listUsers();
      const workerList = (response.users || []).filter(
        (user) => user.UserType === 'WORKER' && !user.IsDeleted
      );
      setWorkers(workerList);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  };

  const handleCreateItem = () => {
    setEditingItem(null);
    itemForm.resetFields();
    setIsItemModalVisible(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    itemForm.setFieldsValue({
      itemName: item.itemName,
      itemType: item.itemType,
      description: item.description,
      totalQuantity: item.totalQuantity,
      unit: item.unit,
      category: item.category,
    });
    setIsItemModalVisible(true);
  };

  const handleSubmitItem = async (values) => {
    setItemSubmitting(true);
    try {
      if (editingItem) {
        await updateInventoryItem(editingItem.itemId, values);
        messageApi.success('Inventory item updated successfully!');
      } else {
        await createInventoryItem(values);
        messageApi.success('Inventory item created successfully!');
      }
      itemForm.resetFields();
      setIsItemModalVisible(false);
      setEditingItem(null);
      fetchItems();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred';
      messageApi.error(errorMessage);
    } finally {
      setItemSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await deleteInventoryItem(itemId);
      messageApi.success('Inventory item archived successfully!');
      fetchItems();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred';
      messageApi.error(errorMessage);
    }
  };

  const handleAssignInventory = (item) => {
    setAssigningItem(item);
    assignForm.resetFields();
    assignForm.setFieldsValue({ quantity: 1 });
    setIsAssignModalVisible(true);
  };

  const handleSubmitAssign = async (values) => {
    setAssignSubmitting(true);
    try {
      await assignInventory({
        itemId: assigningItem.itemId,
        workerMobileNumber: values.workerMobileNumber,
        quantity: values.quantity,
        notes: values.notes,
      });
      messageApi.success('Inventory assigned successfully!');
      assignForm.resetFields();
      setIsAssignModalVisible(false);
      setAssigningItem(null);
      fetchItems();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred';
      messageApi.error(errorMessage);
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleViewAssignments = async (item) => {
    setSelectedItemForAssignments(item);
    setIsAssignmentsModalVisible(true);
    setAssignmentsLoading(true);
    try {
      const response = await getItemAssignments(item.itemId);
      setItemAssignments(response.assignments || []);
    } catch (error) {
      messageApi.error('Failed to fetch item assignments');
      setItemAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const handleReassignInventory = (assignment) => {
    const item = selectedItemForAssignments || items.find(i => i.itemId === assignment.itemId);
    setReassigningData({ 
      item: item,
      assignment: assignment
    });
    reassignForm.resetFields();
    setIsReassignModalVisible(true);
  };

  const handleSubmitReassign = async (values) => {
    setReassignSubmitting(true);
    try {
      await reassignInventory({
        itemId: reassigningData.item.itemId,
        fromWorkerMobileNumber: reassigningData.assignment.workerMobileNumber,
        toWorkerMobileNumber: values.toWorkerMobileNumber,
        assignmentIds: [reassigningData.assignment.assignmentId],
        notes: values.notes,
      });
      messageApi.success('Inventory reassigned successfully!');
      reassignForm.resetFields();
      setIsReassignModalVisible(false);
      const wasViewingAssignments = isAssignmentsModalVisible;
      const itemToRefresh = selectedItemForAssignments;
      setReassigningData(null);
      fetchItems();
      // Refresh assignments view
      if (wasViewingAssignments && itemToRefresh) {
        setTimeout(() => handleViewAssignments(itemToRefresh), 500);
      }
      if (activeTab === 'report') {
        setTimeout(() => fetchReport(), 500);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred';
      messageApi.error(errorMessage);
    } finally {
      setReassignSubmitting(false);
    }
  };

  const handleShowReport = () => {
    setIsReportModalVisible(true);
    fetchReport();
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const filters = {};
      if (reportFilters.itemId) filters.itemId = reportFilters.itemId;
      if (reportFilters.workerMobileNumber)
        filters.workerMobileNumber = reportFilters.workerMobileNumber;
      if (reportFilters.startDate)
        filters.startDate = reportFilters.startDate.format('YYYY-MM-DD');
      if (reportFilters.endDate)
        filters.endDate = reportFilters.endDate.format('YYYY-MM-DD');

      const response = await getInventoryReport(filters);
      setReportData(response);
    } catch (error) {
      messageApi.error('Failed to fetch inventory report');
    } finally {
      setReportLoading(false);
    }
  };

  const filteredItems = items.filter((item) => {
    const searchLower = searchText.toLowerCase();
    return (
      item.itemName.toLowerCase().includes(searchLower) ||
      item.itemType.toLowerCase().includes(searchLower) ||
      (item.category && item.category.toLowerCase().includes(searchLower))
    );
  });

  const itemColumns = [
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
      render: (text, record) => (
        <Space>
          <strong>{text}</strong>
          <Tag color={record.itemType === 'MACHINE' ? 'blue' : 'green'}>
            {record.itemType}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: 'Total Quantity',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      align: 'right',
      render: (text, record) => `${text} ${record.unit || 'piece'}`,
    },
    {
      title: 'Assigned',
      dataIndex: 'assignedQuantity',
      key: 'assignedQuantity',
      align: 'right',
      render: (text, record) => `${text} ${record.unit || 'piece'}`,
    },
    {
      title: 'Available',
      dataIndex: 'availableQuantity',
      key: 'availableQuantity',
      align: 'right',
      render: (text, record) => (
        <Text
          type={text === 0 ? 'danger' : text < 5 ? 'warning' : undefined}
          strong={text === 0}
        >
          {text} {record.unit || 'piece'}
        </Text>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (text) => text || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Assignments">
            <Button
              type="default"
              icon={<BarChartOutlined />}
              size="small"
              onClick={() => handleViewAssignments(record)}
            >
              View ({record.assignedQuantity})
            </Button>
          </Tooltip>
          <Tooltip title="Assign to Worker">
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              size="small"
              onClick={() => handleAssignInventory(record)}
              disabled={record.availableQuantity === 0}
            >
              Assign
            </Button>
          </Tooltip>
          <Button
            type="default"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditItem(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to archive this item?"
            description="This will only work if no items are currently assigned."
            onConfirm={() => handleDeleteItem(record.itemId)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              icon={<DeleteOutlined />}
              size="small"
              danger
            >
              Archive
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const reportColumns = [
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
    },
    {
      title: 'Worker',
      dataIndex: 'workerFullName',
      key: 'workerFullName',
      render: (text, record) => (
        <Space>
          <Text>{text}</Text>
          <Text type="secondary">({record.workerMobileNumber})</Text>
        </Space>
      ),
    },
    {
      title: 'Assigned Date',
      dataIndex: 'assignedAt',
      key: 'assignedAt',
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: 'Assigned By',
      dataIndex: 'assignedBy',
      key: 'assignedBy',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const item = items.find((i) => i.itemId === record.itemId);
        return (
          <Button
            type="link"
            icon={<UserDeleteOutlined />}
            onClick={() => handleReassignInventory(record)}
          >
            Reassign
          </Button>
        );
      },
    },
  ];

  const historyColumns = [
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      key: 'itemName',
    },
    {
      title: 'Worker',
      dataIndex: 'workerFullName',
      key: 'workerFullName',
      render: (text, record) => (
        <Space>
          <Text>{text}</Text>
          <Text type="secondary">({record.workerMobileNumber})</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'ASSIGNED' ? 'green' : 'red'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Assigned Date',
      dataIndex: 'assignedAt',
      key: 'assignedAt',
      render: (text) => (text ? new Date(text).toLocaleDateString() : '-'),
    },
    {
      title: 'Released Date',
      dataIndex: 'releasedAt',
      key: 'releasedAt',
      render: (text) => (text ? new Date(text).toLocaleDateString() : '-'),
    },
  ];

  const tabItems = [
    {
      key: 'items',
      label: 'Inventory Items',
      children: (
        <Table
          columns={itemColumns}
          dataSource={filteredItems}
          rowKey="itemId"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'report',
      label: 'Reports',
      children: (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Filter by Item">
                  <Select
                    placeholder="Select Item"
                    allowClear
                    value={reportFilters.itemId}
                    onChange={(value) =>
                      setReportFilters({ ...reportFilters, itemId: value })
                    }
                  >
                    {items.map((item) => (
                      <Option key={item.itemId} value={item.itemId}>
                        {item.itemName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Filter by Worker">
                  <Select
                    placeholder="Select Worker"
                    allowClear
                    showSearch
                    value={reportFilters.workerMobileNumber}
                    onChange={(value) =>
                      setReportFilters({
                        ...reportFilters,
                        workerMobileNumber: value,
                      })
                    }
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {workers.map((worker) => (
                      <Option
                        key={worker.MobileNumber}
                        value={worker.MobileNumber}
                      >
                        {worker.FullName} ({worker.MobileNumber})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Date Range">
                  <RangePicker
                    value={[
                      reportFilters.startDate,
                      reportFilters.endDate,
                    ]}
                    onChange={(dates) =>
                      setReportFilters({
                        ...reportFilters,
                        startDate: dates ? dates[0] : null,
                        endDate: dates ? dates[1] : null,
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label=" ">
                  <Button
                    type="primary"
                    icon={<BarChartOutlined />}
                    onClick={fetchReport}
                  >
                    Load Report
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Card>
          {reportData && (
            <>
              <Card style={{ marginBottom: 16 }}>
                <Title level={4}>Current Assignments</Title>
                <Table
                  columns={reportColumns}
                  dataSource={reportData.currentAssignments || []}
                  rowKey={(record) =>
                    `${record.itemId}-${record.workerMobileNumber}`
                  }
                  loading={reportLoading}
                  pagination={{ pageSize: 10 }}
                />
              </Card>
              <Card>
                <Title level={4}>Assignment History</Title>
                <Table
                  columns={historyColumns}
                  dataSource={reportData.history || []}
                  rowKey={(record, index) => `${record.itemId}-${index}`}
                  loading={reportLoading}
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <Title level={2}>Inventory Management</Title>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Search items by name, type, or category"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 400 }}
            />
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchItems}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateItem}
              >
                Add Item
              </Button>
              <Button
                type="default"
                icon={<BarChartOutlined />}
                onClick={handleShowReport}
              >
                View Full Report
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />

      {/* Item Create/Edit Modal */}
      <Modal
        title={editingItem ? 'Edit Inventory Item' : 'Create Inventory Item'}
        open={isItemModalVisible}
        onCancel={() => {
          setIsItemModalVisible(false);
          setEditingItem(null);
          itemForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={itemForm}
          layout="vertical"
          onFinish={handleSubmitItem}
        >
          <Form.Item
            name="itemName"
            label="Item Name"
            rules={[
              { required: true, message: 'Please enter item name!' },
            ]}
          >
            <Input placeholder="e.g., Hammer, Drill Machine" />
          </Form.Item>

          <Form.Item
            name="itemType"
            label="Item Type"
            rules={[
              { required: true, message: 'Please select item type!' },
            ]}
          >
            <Select placeholder="Select type">
              <Option value="MACHINE">Machine</Option>
              <Option value="TOOL">Tool</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter item description (optional)" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="totalQuantity"
                label="Total Quantity"
                rules={[
                  { required: true, message: 'Please enter total quantity!' },
                  { type: 'number', min: 1, message: 'Quantity must be at least 1!' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  placeholder="Total quantity"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="unit"
                label="Unit"
                initialValue="piece"
              >
                <Select placeholder="Select unit">
                  <Option value="piece">Piece</Option>
                  <Option value="kg">Kilogram</Option>
                  <Option value="meter">Meter</Option>
                  <Option value="set">Set</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="category"
            label="Category"
          >
            <Input placeholder="e.g., Construction Tools, Safety Equipment" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={itemSubmitting}
              >
                {editingItem ? 'Update' : 'Create'}
              </Button>
              <Button
                onClick={() => {
                  setIsItemModalVisible(false);
                  setEditingItem(null);
                  itemForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Inventory Modal */}
      <Modal
        title={`Assign Inventory: ${assigningItem?.itemName}`}
        open={isAssignModalVisible}
        onCancel={() => {
          setIsAssignModalVisible(false);
          setAssigningItem(null);
          assignForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={assignForm}
          layout="vertical"
          onFinish={handleSubmitAssign}
        >
          <Form.Item
            name="workerMobileNumber"
            label="Worker"
            rules={[
              { required: true, message: 'Please select a worker!' },
            ]}
          >
            <Select
              placeholder="Select worker"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {workers.map((worker) => (
                <Option key={worker.MobileNumber} value={worker.MobileNumber}>
                  {worker.FullName} ({worker.MobileNumber}) - {worker.Job}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[
              { required: true, message: 'Please enter quantity!' },
              {
                type: 'number',
                min: 1,
                max: assigningItem?.availableQuantity || 1,
                message: `Quantity must be between 1 and ${assigningItem?.availableQuantity || 1}!`,
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={assigningItem?.availableQuantity || 1}
              placeholder="Enter quantity"
            />
          </Form.Item>
          <Text type="secondary">
            Available: {assigningItem?.availableQuantity || 0}{' '}
            {assigningItem?.unit || 'piece'}
          </Text>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Optional notes" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={assignSubmitting}
              >
                Assign
              </Button>
              <Button
                onClick={() => {
                  setIsAssignModalVisible(false);
                  setAssigningItem(null);
                  assignForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reassign Inventory Modal */}
      <Modal
        title={`Reassign Inventory: ${reassigningData?.item?.itemName}`}
        open={isReassignModalVisible}
        onCancel={() => {
          setIsReassignModalVisible(false);
          setReassigningData(null);
          reassignForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        {reassigningData && (
          <Form
            form={reassignForm}
            layout="vertical"
            onFinish={handleSubmitReassign}
          >
            <Form.Item label="From Worker">
              <Text>
                {reassigningData.assignment.workerFullName} (
                {reassigningData.assignment.workerMobileNumber})
              </Text>
            </Form.Item>

            <Form.Item label="Assigned Date">
              <Text>
                {new Date(reassigningData.assignment.assignedAt).toLocaleDateString()}
              </Text>
            </Form.Item>

            <Form.Item
              name="toWorkerMobileNumber"
              label="Reassign To Worker"
              rules={[
                { required: true, message: 'Please select a worker!' },
              ]}
            >
              <Select
                placeholder="Select worker"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {workers
                  .filter(worker => worker.MobileNumber !== reassigningData.assignment.workerMobileNumber)
                  .map((worker) => (
                    <Option key={worker.MobileNumber} value={worker.MobileNumber}>
                      {worker.FullName} ({worker.MobileNumber}) - {worker.Job}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item name="notes" label="Notes">
              <TextArea rows={3} placeholder="Optional notes" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={reassignSubmitting}
                >
                  Reassign
                </Button>
                <Button
                  onClick={() => {
                    setIsReassignModalVisible(false);
                    setReassigningData(null);
                    reassignForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* View Assignments Modal */}
      <Modal
        title={`Assignments: ${selectedItemForAssignments?.itemName}`}
        open={isAssignmentsModalVisible}
        onCancel={() => {
          setIsAssignmentsModalVisible(false);
          setSelectedItemForAssignments(null);
          setItemAssignments([]);
        }}
        footer={null}
        width={800}
      >
        {assignmentsLoading ? (
          <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '50px' }} />
        ) : (
          <Table
            columns={[
              {
                title: 'Worker',
                dataIndex: 'workerFullName',
                key: 'workerFullName',
                render: (text, record) => (
                  <Space>
                    <Text>{text}</Text>
                    <Text type="secondary">({record.workerMobileNumber})</Text>
                  </Space>
                ),
              },
              {
                title: 'Assigned Date',
                dataIndex: 'assignedAt',
                key: 'assignedAt',
                render: (text) => new Date(text).toLocaleDateString(),
              },
              {
                title: 'Assigned By',
                dataIndex: 'assignedBy',
                key: 'assignedBy',
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                  <Button
                    type="link"
                    icon={<UserDeleteOutlined />}
                    onClick={() => handleReassignInventory(record)}
                  >
                    Reassign
                  </Button>
                ),
              },
            ]}
            dataSource={itemAssignments}
            rowKey="assignmentId"
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: 'No assignments found' }}
          />
        )}
      </Modal>

      {/* Full Report Modal */}
      <Modal
        title="Inventory Assignment Report"
        open={isReportModalVisible}
        onCancel={() => {
          setIsReportModalVisible(false);
          setReportFilters({
            itemId: null,
            workerMobileNumber: null,
            startDate: null,
            endDate: null,
          });
        }}
        footer={null}
        width={1200}
      >
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Select
                placeholder="Filter by Item"
                allowClear
                style={{ width: '100%' }}
                value={reportFilters.itemId}
                onChange={(value) =>
                  setReportFilters({ ...reportFilters, itemId: value })
                }
              >
                {items.map((item) => (
                  <Option key={item.itemId} value={item.itemId}>
                    {item.itemName}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="Filter by Worker"
                allowClear
                showSearch
                style={{ width: '100%' }}
                value={reportFilters.workerMobileNumber}
                onChange={(value) =>
                  setReportFilters({
                    ...reportFilters,
                    workerMobileNumber: value,
                  })
                }
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {workers.map((worker) => (
                  <Option
                    key={worker.MobileNumber}
                    value={worker.MobileNumber}
                  >
                    {worker.FullName}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <RangePicker
                style={{ width: '100%' }}
                value={[reportFilters.startDate, reportFilters.endDate]}
                onChange={(dates) =>
                  setReportFilters({
                    ...reportFilters,
                    startDate: dates ? dates[0] : null,
                    endDate: dates ? dates[1] : null,
                  })
                }
              />
            </Col>
            <Col span={4}>
              <Button
                type="primary"
                icon={<BarChartOutlined />}
                onClick={fetchReport}
                block
              >
                Load
              </Button>
            </Col>
          </Row>
        </Card>
        {reportLoading ? (
          <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '50px' }} />
        ) : reportData ? (
          <>
            <Card style={{ marginBottom: 16 }}>
              <Title level={4}>Current Assignments</Title>
              <Table
                columns={reportColumns}
                dataSource={reportData.currentAssignments || []}
                rowKey={(record) => record.assignmentId || `${record.itemId}-${record.workerMobileNumber}-${record.assignedAt}`}
                pagination={{ pageSize: 10 }}
              />
            </Card>
            <Card>
              <Title level={4}>Assignment History</Title>
              <Table
                columns={historyColumns}
                dataSource={reportData.history || []}
                rowKey={(record, index) => record.assignmentId || `${record.itemId}-${index}`}
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </>
        ) : (
          <Text>Click "Load" to fetch report data</Text>
        )}
      </Modal>
    </div>
  );
};

export default InventoryManagement;

